import type { Notification } from '~/types/common/notification';

/**
 * [MCV-1918] Alerts about the same listing stack under one row. `items` is
 * newest first and `latest` is `items[0]`; a notification without a listing
 * reference is a group of one.
 */
export type NotificationGroup = {
  key: string;
  latest: Notification;
  items: Notification[];
};

const isPresent = (value: unknown): value is string | number =>
  (typeof value === 'string' && value.trim() !== '') || typeof value === 'number';

/** `listingType:listingId` from the server's `extraData`, or null when the alert names no listing. */
const listingKey = ({ extraData }: Notification): string | null => {
  const listingType = extraData?.listingType;
  const listingId = extraData?.listingId;
  if (!isPresent(listingType) || !isPresent(listingId)) return null;
  return `${listingType}:${listingId}`;
};

const timeOf = (notification: Notification): number => {
  const time = new Date(notification.createdAt).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const newestFirst = (a: Notification, b: Notification): number =>
  timeOf(b) - timeOf(a) || b.id - a.id;

export const groupNotifications = (notifications: Notification[]): NotificationGroup[] => {
  const groups = new Map<string, NotificationGroup>();
  // Walking newest first means the first alert seen for a key is its latest,
  // so Map insertion order is already "groups ordered by their latest item".
  for (const notification of [...notifications].sort(newestFirst)) {
    const key = listingKey(notification) ?? `single:${notification.id}`;
    const group = groups.get(key);
    if (group) {
      group.items.push(notification);
    } else {
      groups.set(key, { key, latest: notification, items: [notification] });
    }
  }
  return [...groups.values()];
};
